<script setup>
// RM-400 — "Booked?": the member says where their group/private dining inquiry
// ended up. Three things and nothing else: which of the inquiry's restaurants,
// the time, and the signed contract or final receipt - optional since 10/21
// (Kapil: RM follows up by email for it). The time opens on the time they
// asked for, earlier and later still there. Same write as RM AI's
// mark_inquiry_booked (POST /reservation/:id/booked).
import { computed, ref, watch } from 'vue'
import moment from 'moment'
import { useReservationsStore } from '@/stores/reservations.store'

const props = defineProps({
  reservation: { type: Object, required: true },
  modelValue: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'booked'])

const reservationsStore = useReservationsStore()

const restaurant = ref('')
const time = ref('')
const file = ref(null)
const fileInputRef = ref(null)
const saving = ref(false)
const error = ref('')

const restaurants = computed(() => props.reservation?.requestedRestaurantsArray || [])
const dateLabel = computed(() => (props.reservation?.date ? moment(props.reservation.date, 'YYYY-MM-DD').format('ddd M/D/YY') : ''))

// 15-minute start times, as the request form offers them.
const timeOptions = computed(() => {
  const out = []
  for (let h = 11; h < 24; h += 1) {
    for (const m of ['00', '15', '30', '45']) {
      const hour12 = h % 12 === 0 ? 12 : h % 12
      out.push(`${hour12}:${m}${h < 12 ? 'am' : 'pm'}`)
    }
  }
  
  return out
})

const canSubmit = computed(() => !!restaurant.value && !!time.value && !saving.value)

// The time the member asked for, in the select's own format ("5:30pm"), so the list opens there.
const askedTime = computed(() => {
  const raw = String(props.reservation?.preferredTime || '').trim()
  if (!raw) return ''
  const m = moment(raw, ['h:mma', 'h:mm a', 'hh:mm a', 'H:mm', 'h a', 'ha'], true)
  
  return m.isValid() ? m.format('h:mma') : ''
})
const timeItems = computed(() => (askedTime.value && !timeOptions.value.includes(askedTime.value)
  ? [...timeOptions.value, askedTime.value].sort((a, b) => moment(a, 'h:mma').diff(moment(b, 'h:mma')))
  : timeOptions.value))

watch(() => props.modelValue, open => {
  if (open) {
    time.value = askedTime.value || ''
    error.value = ''
  }
}, { immediate: true })

const isOpen = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

function pickFile(event) {
  const picked = event?.target?.files?.[0] || null
  error.value = ''
  file.value = picked
}

function clearFile() {
  file.value = null
  if (fileInputRef.value) fileInputRef.value.value = ''
}

async function submit() {
  if (!canSubmit.value) return
  saving.value = true
  error.value = ''
  try {
    const mediaIds = []
    if (file.value) {
      const formData = new FormData()
      formData.append('image', file.value)
      const upload = await fetch('/media/', { method: 'POST', body: formData, credentials: 'include' })
      if (!upload.ok) throw new Error('upload_failed')
      const imageId = (await upload.text()).trim()
      if (!imageId) throw new Error('upload_failed')
      mediaIds.push(imageId)
    }
    await reservationsStore.markInquiryBooked(props.reservation._id || props.reservation.id, {
      restaurant: restaurant.value,
      time: time.value,
      mediaIds,
    })
    emit('booked')
    isOpen.value = false
  } catch (err) {
    const code = err?.response?.data?.context || err?.response?.data?.code || err?.message
    error.value = err?.response?.data?.message
      || (code === 'upload_failed' ? 'The file could not be uploaded. Please try again, or email it to preferred@reserverm.com' : 'Something went wrong. Please try again')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <VDialog
    v-model="isOpen"
    max-width="440"
    content-class="inquiry-booked-dialog"
    transition="dialog-bottom-transition"
  >
    <VCard class="inquiry-booked">
      <div class="inquiry-booked__handle" />
      <button
        type="button"
        class="inquiry-booked__close"
        :aria-label="$t('reservation.private.close')"
        @click="isOpen = false"
      >
        ✕
      </button>
      <h2 class="inquiry-booked__title">
        {{ $t('reservation.private.whereBooked') }}
      </h2>
      <p class="inquiry-booked__sub">
        {{ dateLabel }} · {{ reservation.partySize }} {{ $t('reservation.private.guests') }}
      </p>

      <VRadioGroup
        v-model="restaurant"
        class="inquiry-booked__restaurants"
        hide-details
      >
        <VRadio
          v-for="name in restaurants"
          :key="name"
          :label="name"
          :value="name"
          color="primary-dark"
          class="inquiry-booked__restaurant"
        />
      </VRadioGroup>

      <div class="inquiry-booked__field">
        <VSelect
          v-model="time"
          :items="timeItems"
          :label="$t('reservation.private.time')"
          variant="underlined"
          hide-details
          class="inquiry-booked__select"
        />
      </div>

      <div class="inquiry-booked__field">
        <div class="inquiry-booked__label">
          {{ $t('reservation.private.proofLabel') }}
        </div>
        <input
          ref="fileInputRef"
          type="file"
          accept="image/*,application/pdf"
          class="inquiry-booked__file-input"
          @change="pickFile"
        >
        <button
          v-if="!file"
          type="button"
          class="inquiry-booked__attach"
          @click="fileInputRef?.click()"
        >
          <VIcon
            size="18"
            class="mr-2"
          >
            mdi-paperclip
          </VIcon>
          {{ $t('reservation.private.addFile') }}
        </button>
        <div
          v-else
          class="inquiry-booked__attach inquiry-booked__attach--filled"
        >
          <span class="inquiry-booked__file">
            <VIcon
              size="18"
              class="mr-2"
            >
              mdi-file-outline
            </VIcon>
            {{ file.name }}
          </span>
          <button
            type="button"
            class="inquiry-booked__remove"
            aria-label="Remove file"
            @click="clearFile"
          >
            ✕
          </button>
        </div>
        <p class="inquiry-booked__help">
          {{ $t('reservation.private.proofHelp') }}
        </p>
      </div>

      <p
        v-if="error"
        class="inquiry-booked__error"
      >
        {{ error }}
      </p>

      <VBtn
        color="primary-dark"
        size="large"
        block
        rounded="pill"
        class="inquiry-booked__submit"
        :disabled="!canSubmit"
        :loading="saving"
        @click="submit"
      >
        {{ $t('reservation.private.addToBookings') }}
      </VBtn>
    </VCard>
  </VDialog>
</template>

<style>
/* The sheet rises from the bottom on a phone (the dialog's content box is outside this component's scope). */
.inquiry-booked-dialog {
  align-self: flex-end !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: 480px !important;
}
@media (min-width: 600px) {
  .inquiry-booked-dialog {
    align-self: center !important;
    margin: 24px !important;
  }
}
</style>

<style scoped>
.inquiry-booked {
  position: relative;
  display: block;
  padding: 8px 16px 16px;
  border-radius: 16px 16px 0 0;
  max-height: 92vh;
  overflow-y: auto;
}
.inquiry-booked__handle {
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: #d9d9de;
  margin: 0 auto 12px;
}
.inquiry-booked__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  font-size: 18px;
  line-height: 40px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  cursor: pointer;
}
.inquiry-booked__title {
  font-family: var(--ds-font-heading);
  font-weight: 500;
  font-size: 20px;
  color: var(--ds-brand-ink);
  margin: 0 0 4px;
}
.inquiry-booked__sub {
  font-size: 12.5px;
  color: var(--ds-text-muted);
  margin: 0 0 4px;
}
.inquiry-booked__restaurant {
  min-height: 40px;
  border-bottom: 1px solid var(--ds-border-subtle);
}
.inquiry-booked__restaurant :deep(.v-label) {
  font-size: 16px;
  color: var(--ds-text-primary);
  opacity: 1;
}
.inquiry-booked__field {
  margin-top: 12px;
}
.inquiry-booked__label {
  font-size: 14px;
  color: var(--ds-text-body);
  margin-bottom: 4px;
}
.inquiry-booked__select :deep(input),
.inquiry-booked__select :deep(.v-select__selection-text) {
  font-size: 16px;
}
.inquiry-booked__file-input {
  display: none;
}
.inquiry-booked__attach {
  width: 100%;
  margin-top: 8px;
  border: 1px dashed #c9c9d0;
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: var(--ds-text-primary);
  background: transparent;
  text-align: left;
}
.inquiry-booked__file {
  display: inline-flex;
  align-items: center;
}
.inquiry-booked__remove {
  background: none;
  border: 0;
  color: var(--ds-text-muted);
  font-size: 14px;
  min-width: 44px;
  min-height: 24px;
}
.inquiry-booked__help {
  font-size: 12.5px;
  color: var(--ds-text-muted);
  line-height: 1.5;
  margin: 6px 0 0;
}
.inquiry-booked__error {
  font-size: 13.5px;
  color: #b3261e;
  margin: 12px 0 0;
}
.inquiry-booked__submit {
  margin-top: 14px;
  height: 44px;
  font-size: 16px;
  text-transform: none;
}
</style>
